import { useEffect, useState } from "react";
import { FileText, BookOpen, Target, CalendarClock, Printer, CheckCircle2, Circle } from "lucide-react";
import api from "../../api";

// "What the patient gets from this session" on the Shared with Patient tab of
// a non-prescriber's mental-health note. Read-only: each item comes from its
// own tab (backend clinical/notes/visit_summary.py), and together they make the
// Visit Summary the patient downloads.
const fmtDay = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }) : "");
const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return new Date(2000, 0, 1, h, m || 0).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
};

function Row({ icon: Icon, label, done, children, action }) {
  return (
    <div className="flex items-start gap-2.5 py-2 border-t border-emerald-100 first:border-t-0">
      {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> : <Circle className="w-4 h-4 text-secondary-300 mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary-800"><Icon className="w-3.5 h-3.5 text-secondary-500" />{label}</div>
        <div className="text-xs text-secondary-600 mt-0.5">{children}</div>
      </div>
      {action}
    </div>
  );
}

export default function PatientTakeawayPreview({ patientId, session, summaryText, tabKeys = [], onGoToTab, onPrint, refreshKey }) {
  const [vs, setVs] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    api.getVisitSummary(patientId, { consultation_id: session?.consultationId || undefined, appointment_id: session?.appointmentId || undefined })
      .then((r) => setVs(r.data))
      .catch(() => setVs(null));
  }, [patientId, session?.consultationId, session?.appointmentId, refreshKey]);

  const homework = vs?.homework || [];
  const goals = vs?.goals || [];
  const next = vs?.next_visit;
  // Only offer a tab that's on this page (homework/plan follow their features).
  const link = (tab, text) => onGoToTab && tabKeys.includes(tab) && (
    <button type="button" onClick={() => onGoToTab(tab)} className="text-[11px] font-semibold text-primary-600 hover:text-primary-800 whitespace-nowrap cursor-pointer">{text}</button>
  );

  return (
    <div className="rounded-lg border border-emerald-200 bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div>
          <p className="text-xs font-bold text-emerald-900">What the patient gets from this session</p>
          <p className="text-[11px] text-secondary-500">This makes up their Visit Summary. Diagnosis is left off unless you choose it when printing.</p>
        </div>
        {onPrint && (
          <button type="button" onClick={onPrint} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 px-2.5 py-1 rounded-md cursor-pointer">
            <Printer className="w-3.5 h-3.5" /> Print visit summary
          </button>
        )}
      </div>
      <Row icon={FileText} label="Summary of the session" done={Boolean(summaryText?.trim())}>
        {summaryText?.trim() ? "From the box above." : "Not written yet. Write a few plain lines above."}
      </Row>
      <Row icon={BookOpen} label="To work on before next time" done={homework.length > 0} action={link("homework", homework.length ? "Change" : "Assign")}>
        {homework.length ? homework.map((h) => h.title_en || h.title).join(", ") : "No homework given in this session."}
      </Row>
      <Row icon={Target} label="Goals we're working on" done={goals.length > 0} action={link("plan", goals.length ? "Open plan" : "Agree a plan")}>
        {goals.length ? goals.map((g) => g.goal).join(" · ") : "No plan shared with the patient yet."}
      </Row>
      <Row icon={CalendarClock} label="Next visit" done={Boolean(next)}>
        {next ? `${fmtDay(next.date)}${next.time ? `, ${fmtTime(next.time)}` : ""}${next.mode ? ` · ${next.mode}` : ""}` : "Not booked yet."}
      </Row>
    </div>
  );
}
